import { CheckCircle as UiCheckCircle, XCircle as UiXCircle } from '../components/icons';
// frontend/src/pages/Login.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import OtpInput from '../components/OtpInput';
import useSmsOtp from '../hooks/useSmsOtp';
import { normalizeOtp, otpDigits } from '../utils/otp';
import { normalizePhone } from '../utils/phone';

export default function Login() {
  const [activeTab, setActiveTab] = useState('password');
  const [form, setForm] = useState({ phone_number: '', password: '' });
  const [code, setCode] = useState(['', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();
  const { start: startSmsOtp, cancel: cancelSmsOtp } = useSmsOtp(value => setCode(otpDigits(value)));
  
  // تایمر
  useEffect(() => {
    let interval;
    if (isCodeSent && timer > 0 && activeTab === 'code') {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer <= 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [isCodeSent, timer, activeTab]);

  const handleChange = (e) => {
    const {name,value}=e.target;
    setForm(current=>({ ...current, [name]: name==='phone_number'?normalizePhone(value):value }));
  };



  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const res = await api.post('/api/auth/login', form);
      const { accessToken, refreshToken, user } = res.data;

      localStorage.setItem('authToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('userInfo', JSON.stringify(user));

      setSuccess(`خوش آمدید ${user.name || user.first_name}`);
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'شماره تلفن یا رمز عبور اشتباه است');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    setCode(otpDigits(''));
    startSmsOtp();
    try {
      await api.post('/api/auth/login/request-code', {
        phone_number: form.phone_number,
        otp_host: window.location.hostname,
      });
      setIsCodeSent(true);
      setTimer(60);
      setCanResend(false);
      setSuccess('کد تأیید ارسال شد');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      cancelSmsOtp();
      setError(err.response?.data?.message || 'خطا در ارسال کد');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setSuccess('');
    setCode(otpDigits(''));
    startSmsOtp();
    try {
      await api.post('/api/auth/login/request-code', {
        phone_number: form.phone_number,
        otp_host: window.location.hostname,
      });
      setTimer(60);
      setCanResend(false);
      setSuccess('کد جدید ارسال شد');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      cancelSmsOtp();
      setError(err.response?.data?.message || 'خطا در ارسال مجدد');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleVerifyCode = async (e, suppliedCode) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const verificationCode = normalizeOtp(suppliedCode || code.join(''));
    if (verificationCode.length !== 5) {
      setError('کد ۵ رقمی را کامل وارد کنید');
      setTimeout(() => setError(''), 3000);
      return;
    }

    cancelSmsOtp();
    try {
      const res = await api.post('/api/auth/verify', {
        phone_number: form.phone_number,
        code: verificationCode,
      });
      const { accessToken, refreshToken, user } = res.data;

      localStorage.setItem('authToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('userInfo', JSON.stringify(user));

      setSuccess(`خوش آمدید ${user.name || user.first_name}`);
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'کد اشتباه است');
      setTimeout(() => setError(''), 3000);
    }
  };

  // --- پیام‌ها ---
  const Toast = ({ type, message }) => (
    <motion.div
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -100, opacity: 0 }}
      transition={{ type: "spring", stiffness: 300 }}
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-6 py-4 rounded-2xl shadow-2xl border flex items-center gap-3 ${
        type === 'error' ? 'bg-red-500/90 text-white border-red-300' : 'bg-green-500/90 text-white border-green-300'
      } backdrop-blur-lg`}
    >
      {type === 'error' ? <UiXCircle className="w-6 h-6" /> : <UiCheckCircle className="w-6 h-6" />}
      <span className="font-medium">{message}</span>
    </motion.div>
  );

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center px-4 py-8 font-vazir">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 100 }}
          className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-8 w-full max-w-md border border-white/30"
        >

          {/* عنوان */}
          <motion.h2 data-page-title="true"
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            className="text-3xl font-bold text-center bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-8"
          >
            ورود به حساب
          </motion.h2>

          {/* تب‌ها */}
          <div className="flex justify-center mb-8">
            <div className="bg-gray-100 p-1 rounded-2xl flex gap-1">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => { cancelSmsOtp(); setCode(otpDigits('')); setIsCodeSent(false); setActiveTab('password'); }}
                className={`px-6 py-3 rounded-xl font-medium transition-all ${
                  activeTab === 'password'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg'
                    : 'text-gray-600'
                }`}
              >
                رمز عبور
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => { cancelSmsOtp(); setCode(otpDigits('')); setIsCodeSent(false); setActiveTab('code'); }}
                className={`px-6 py-3 rounded-xl font-medium transition-all ${
                  activeTab === 'code'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg'
                    : 'text-gray-600'
                }`}
              >
                کد تأیید
              </motion.button>
            </div>
          </div>

          {/* فرم‌ها */}
          <AnimatePresence mode="wait">
            {activeTab === 'password' ? (
              <motion.form
                key="password"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                onSubmit={handlePasswordLogin}
                className="space-y-5"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">شماره تلفن</label>
                  <motion.input
  whileFocus={{ scale: 1.02 }}
  name="phone_number"
  type="text"
  value={form.phone_number}
  onChange={handleChange}
  required
  placeholder="۰۹۱۲۳۴۵۶۷۸۹"
  disabled={isCodeSent}
  autoComplete="tel"
  inputMode="numeric"
  dir="ltr"
  className="w-full px-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 transition-all bg-gray-50 disabled:bg-gray-100"
/>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">رمز عبور</label>
                  <motion.input
                    whileFocus={{ scale: 1.02 }}
                    name="password"
                    type="password"
                    value={form.password}
                    onChange={handleChange}
                    required
                    placeholder="••••••••"
                    className="w-full px-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 transition-all bg-gray-50"
                  />
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-2xl font-bold shadow-lg hover:shadow-xl transition-all"
                >
                  ورود با رمز عبور
                </motion.button>
              </motion.form>
            ) : (
              <motion.form
                key="code"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={isCodeSent ? handleVerifyCode : handleRequestCode}
                className="space-y-6"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">شماره تلفن</label>
                  <motion.input
                    whileFocus={{ scale: 1.02 }}
                    name="phone_number"
                    type="text"
                    autoComplete="tel"
                    inputMode="numeric"
                    dir="ltr"
                    value={form.phone_number}
                    onChange={handleChange}
                    required
                    placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                    disabled={isCodeSent}
                    className="w-full px-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 transition-all bg-gray-50 disabled:bg-gray-100"
                  />
                </div>

                {isCodeSent && (
                  <>
                    <p className="text-center text-sm text-gray-600">
                      کد ۵ رقمی به <span className="font-mono text-indigo-700">{form.phone_number}</span> ارسال شد
                    </p>
                    <OtpInput value={code.join('')} onChange={value => setCode(otpDigits(value))} disabled={false} />

                    <div className="text-center">
                      {!canResend ? (
                        <p className="text-sm text-gray-500">{timer} ثانیه تا ارسال مجدد</p>
                      ) : (
                        <motion.button
                          type="button"
                          whileHover={{ scale: 1.05 }}
                          onClick={handleResendCode}
                          className="text-indigo-600 font-medium underline"
                        >
                          ارسال مجدد کد
                        </motion.button>
                      )}
                    </div>
                  </>
                )}

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-2xl font-bold shadow-lg hover:shadow-xl transition-all"
                >
                  {isCodeSent ? 'تأیید کد' : 'ارسال کد تأیید'}
                </motion.button>
              </motion.form>
            )}
          </AnimatePresence>

          {/* ثبت‌نام */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center mt-8"
          >
            <p className="text-sm text-gray-600 mb-2">هنوز حساب ندارید؟</p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/register_page')}
              className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-8 py-3 rounded-full font-bold shadow-lg hover:shadow-xl transition-all"
            >
              ثبت‌نام کنید
            </motion.button>
          </motion.div>
        </motion.div>

        {/* پیام‌ها */}
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-sm px-4">
          <AnimatePresence>
            {error && <Toast type="error" message={error} />}
            {success && <Toast type="success" message={success} />}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}
